import Request from '../Request';
import TopSellersApi, { DropdownOptions } from './TopSellers.service';
import type {
  ClientSettingsRow,
  CoreReplenishmentApiResponse,
  GetCoreReplenishmentParams,
  PinState,
} from '../../components/reports/coreReplenishment/coreReplenishment.types';

export type { DropdownOption, DropdownOptions } from './TopSellers.service';
export type {
  ClassPlanRow,
  ClientSettingsRow,
  CoreReplenishmentApiResponse,
  CoreReplenishmentProductRow,
  CoreReplenishmentSkuRow,
  GetCoreReplenishmentParams,
  PinState,
} from '../../components/reports/coreReplenishment/coreReplenishment.types';

const BASE_URL = '/api/reports/core-replenishment';

export function buildQuery(params: GetCoreReplenishmentParams) {
  const { locationCodes, classCodes, vendorIds, categoryIds } = params;
  return {
    client_id: params.clientId,
    from: params.from,
    to: params.to,
    on_order_span: params.onOrderSpan,
    on_order_start_date: params.onOrderSpan === 'custom' ? params.spanStart || undefined : undefined,
    on_order_end_date: params.onOrderSpan === 'custom' ? params.spanEnd || undefined : undefined,
    location_ids: locationCodes?.length ? locationCodes : undefined,
    class_ids: classCodes?.length ? classCodes : undefined,
    vendor_ids: vendorIds?.length ? vendorIds : undefined,
    category_ids: categoryIds?.length ? categoryIds : undefined,
    exclude_locations: params.excludeLocations && locationCodes?.length ? 1 : undefined,
    exclude_classes: params.excludeClasses && classCodes?.length ? 1 : undefined,
    exclude_vendors: params.excludeVendors && vendorIds?.length ? 1 : undefined,
    exclude_categories: params.excludeCategories && categoryIds?.length ? 1 : undefined,
    by_location: params.byLocation ? 1 : 0,
  };
}

/** GET /api/reports/core-replenishment -- products with SKUs nested, the class plans and client settings. */
function getCoreReplenishment(params: GetCoreReplenishmentParams): Promise<CoreReplenishmentApiResponse> {
  return Request.get(BASE_URL, buildQuery(params), false, true);
}

/** Sets core_item_pins for everyone at the client; null clears the row back to detection. */
async function setPinState(clientId: string | number, productId: string, pinState: PinState | null): Promise<void> {
  await Request.post(`${BASE_URL}/pins`, { client_id: clientId, product_id: productId, pin_state: pinState });
}

/** Saves core_replenishment_settings for the client and returns the stored row. */
async function saveClientSettings(clientId: string | number, settings: ClientSettingsRow): Promise<ClientSettingsRow> {
  const res = await Request.post(`${BASE_URL}/settings`, { client_id: clientId, ...settings });
  return res?.settings ?? settings;
}

function getDropdownOptions(clientId: string | number): Promise<DropdownOptions> {
  return TopSellersApi.getDropdownOptions(clientId);
}

async function getVendorOptions(clientId: string | number): Promise<string[]> {
  const res = await Request.get(`${BASE_URL}/vendors`, { client_id: clientId }, false, true);
  return res?.vendors ?? [];
}

const CoreReplenishmentApi = {
  getCoreReplenishment,
  setPinState,
  saveClientSettings,
  getDropdownOptions,
  getVendorOptions,
};

export default CoreReplenishmentApi;
